import {
  BodyText,
  Button,
  HeaderText,
  Separator,
  Tooltip,
} from "@/components/ui";
import { useMedicalRecordsPreferences } from "@/features/AccountSettings/hooks/useMedicalRecordsPreferences";
import { useNavigate } from "@tanstack/react-router";
import { Settings } from "lucide-react";
import { SectionContent } from "../components/SectionContent";
import { SectionHeader } from "../components/SectionHeader";

import { Alerts } from "../Alerts/Alerts";
import { ExternalCareTeam } from "../ExternalCareTeam/ExternalCareTeam";
import { FamilyMemberHistory } from "../FamilyMemberHistory/FamilyMemberHistory";
import { FilesWithProvider } from "../Files";
import { Goals } from "../Goals/Goals";
import { HealthInsurance } from "../HealthInsurance/HealthInsurance";
import { HealthMaintenance } from "../HealthMaintenance/HealthMaintenance";
import { Hospitalizations } from "../Hospitalization/Hospitalizations";
import { Immunizations } from "../Immunization/Immunizations";
import { Labs } from "../Labs/Labs";
import { MedicalHistory } from "../MedicalHistory/MedicalHistory";
import { MedicationsAllergies } from "../MedicationsAllergies/MedicationsAllergies";
import { Profile } from "../Profile/Profile";
import { SexualHistory } from "../SexualHistory/SexualHistory";
import { SocialHistory } from "../SocialHistory/SocialHistory";
import { SurgicalHistory } from "../SurgicalHistory/SurgicalHistory";
// Import all the medical record section components
import { Timeline } from "../Timeline";
import { Vitals } from "../Vitals/Vitals";
import { WomensHealth } from "../WomensHealth/WomensHealth";

// Create a mapping from section keys to their components
const SECTION_COMPONENT_MAP: Record<string, React.ComponentType> = {
  timeline: Timeline,
  profile: Profile,
  vitals: Vitals,
  "medical-history": MedicalHistory,
  "medications-allergies": MedicationsAllergies,
  "social-history": SocialHistory,
  "sexual-history": SexualHistory,
  "family-history": FamilyMemberHistory,
  "womens-health": WomensHealth,
  hospitalizations: Hospitalizations,
  immunizations: Immunizations,
  labs: Labs,
  "surgical-history": SurgicalHistory,
  "health-maintenance": HealthMaintenance,
  goals: Goals,
  "health-insurance": HealthInsurance,
  "external-care-team": ExternalCareTeam,
  files: FilesWithProvider,
  alerts: Alerts,
};

export const QuickView = () => {
  const navigate = useNavigate();
  const { selectedSections, selectedSectionConfigs } =
    useMedicalRecordsPreferences();

  if (selectedSections.length === 0) {
    return (
      <div className="w-full h-full flex flex-col">
        <SectionHeader title="Quick View" />
        <SectionContent>
          <div className="text-center py-12 flex flex-col items-center gap-2">
            <HeaderText size="md" className="mb-2">
              No Sections Selected
            </HeaderText>
            <BodyText
              size="sm"
              textColor="secondary"
              className="mb-6 max-w-md mx-auto"
            >
              You haven't selected any sections for your Quick view yet. Choose
              up to 5 sections in your account settings to get started.
            </BodyText>
            <Button
              size="sm"
              className="flex flex-row items-center gap-2 w-1/2 mx-auto"
              onClick={() => navigate({ to: "/account-settings" })}
            >
              Configure Sections
            </Button>
          </div>
        </SectionContent>
      </div>
    );
  }

  return (
    <div className="w-full h-full flex flex-col">
      <div className="flex items-center justify-between p-3 border border-b-0 rounded-t-md">
        <div className="flex items-center gap-2">
          <HeaderText textColor="tertiary" size="sm">
            Quick View
          </HeaderText>
          <BodyText textColor="tertiary" size="xs">
            ({selectedSections.length} of 5 sections)
          </BodyText>
        </div>
        <Tooltip tooltipContentText="Settings" side="right">
          <Button
            variant="ghost"
            size="sm"
            className="flex items-center gap-1"
            onClick={() => navigate({ to: "/account-settings" })}
          >
            <Settings size={12} />
          </Button>
        </Tooltip>
      </div>

      <div className="border rounded-b bg-white h-full overflow-y-auto w-full p-3">
        <div className="space-y-6">
          {selectedSectionConfigs.map((sectionConfig, index) => {
            const SectionComponent = SECTION_COMPONENT_MAP[sectionConfig.key];

            if (!SectionComponent) {
              return null;
            }

            return (
              <div key={sectionConfig.key}>
                <SectionComponent />
                {index < selectedSectionConfigs.length - 1 && (
                  <div className="my-6">
                    <Separator />
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};
